import { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { getInventoryRailcars, type RailCar } from '@/services/inventory/inventoryService';

interface Props {
  source: string;
  orderNumber: string;
  itemNumber: string;
  plantCode: string;
  materialCode: string;
  unitOfMeasure: string;
  onClose: () => void;
}

export function AvailableRailcarsDialog({ source, orderNumber, itemNumber, plantCode, materialCode, unitOfMeasure, onClose }: Props) {
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<RailCar[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const pageSize = 5;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(false); setRows([]); setCount(0);
    void getInventoryRailcars({
      source, orderNumber, itemNumber, unitOfMeasure,
      pageNumber: page + 1, linesPerPage: pageSize, searchFilters: null,
      filters: [
        { columnName: 'Location', value: plantCode },
        { columnName: 'MaterialCode', value: materialCode },
      ],
      sorting: [
        { columnName: 'RailCarStatusPriority', value: 'ASC' },
        { columnName: 'BatchId', value: 'DESC' },
        { columnName: 'GrossWeight', value: 'DESC' },
      ],
    }, controller.signal).then((data) => {
      if (controller.signal.aborted) return;
      setRows(data.data ?? []); setCount(data.count ?? data.data?.length ?? 0);
    }).catch(() => {
      if (!controller.signal.aborted) setError(true);
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [source, orderNumber, itemNumber, plantCode, materialCode, unitOfMeasure, page, retry]);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" aria-labelledby="available-railcars-title">
      <DialogTitle id="available-railcars-title">Available railcars — Order {orderNumber}</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>Supply point: {plantCode} · Material: {materialCode}</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>Read-only lookup. Railcar assignment is not available in the current API.</Alert>
        {loading ? <Box role="status" aria-label="Loading available railcars" sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>
          : error ? <Alert severity="error" action={<Button onClick={() => setRetry((value) => value + 1)}>Retry</Button>}>Unable to load available railcars.</Alert>
            : <>
              <Table size="small" aria-label="Available railcars">
                <TableHead><TableRow>{['Railcar ID', 'Batch ID', 'Status', 'Gross weight', 'UoM', 'Reserved by'].map((label) => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
                <TableBody>
                  {rows.map((row, index) => <TableRow key={`${row.source}|${row.railCarId}|${row.batchId}|${index}`}>
                    <TableCell>{row.railCarId || '-'}</TableCell><TableCell>{row.batchId || '-'}</TableCell>
                    <TableCell>{row.railCarStatus || '-'}</TableCell>
                    <TableCell>{row.grossWeight?.toLocaleString('en-US', { maximumFractionDigits: 1 }) ?? '-'}</TableCell>
                    <TableCell>{row.unitOfMeasure || '-'}</TableCell><TableCell>{row.reservedBy || '-'}</TableCell>
                  </TableRow>)}
                  {!rows.length && <TableRow><TableCell colSpan={6}>No available railcars found.</TableCell></TableRow>}
                </TableBody>
              </Table>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mt: 2 }}>
                <Typography>{count ? `${page * pageSize + 1}-${Math.min((page + 1) * pageSize, count)} of ${count}` : '0 of 0'}</Typography>
                <Button disabled={page === 0} onClick={() => setPage((value) => value - 1)}>Previous</Button>
                <Button disabled={(page + 1) * pageSize >= count} onClick={() => setPage((value) => value + 1)}>Next</Button>
              </Box>
            </>}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}